import type { PrV2GithubReviewSide } from '@/lib/pr-v2/types';
import type { SelectedLineRange } from '@pierre/diffs';
import { create } from 'zustand';

export type PrV2FileDraftState = {
  /** Draft body text authored by the user. */
  comment: string;
  /** End line for the pending inline comment. Empty string means file-level comment. */
  line: string;
  /** Side for `line` in GitHub's API shape. */
  side: PrV2GithubReviewSide | null;
  /** Optional start line when selecting a range. */
  startLine: string;
  /** Side for `startLine` in GitHub's API shape. */
  startSide: PrV2GithubReviewSide | null;
  /** Raw range from the diff viewer for rendering and selection persistence. */
  selectedLines: SelectedLineRange | null;
  /** True when a drag selection is stored but has not been promoted into an open inline draft. */
  selectionOnly?: boolean;
};

/** Map of filename to that file's draft state within a single PR namespace. */
type PrV2DraftsByFile = Record<string, PrV2FileDraftState>;

/** Top-level map of `owner/repo/number` -> per-file draft states. */
type PrV2DraftsByPr = Record<string, PrV2DraftsByFile>;

/** Zustand contract for managing PR V2 inline and file-level draft comments. */
type PrV2DraftStore = {
  /**
   * Drafts are scoped by PR key first, then by filename.
   * This lets multiple PR tabs coexist without draft collisions.
   */
  draftsByPr: PrV2DraftsByPr;
  getDraft: (prKey: string, filename: string) => PrV2FileDraftState | undefined;
  /** Opens the file-level composer for a file while preserving any typed body text. */
  openFileDraft: (prKey: string, filename: string) => void;
  /** Toggles an inline draft on a specific line/side pair. Clicking the same target closes it. */
  toggleLineDraft: (
    prKey: string,
    filename: string,
    lineNumber: number,
    side: PrV2GithubReviewSide,
  ) => void;
  /** Persists drag-selection metadata used for multi-line comment creation. */
  setLineSelection: (prKey: string, filename: string, range: SelectedLineRange | null) => void;
  setDraftComment: (prKey: string, filename: string, value: string) => void;
  /** Replaces or clears a file draft atomically, used for optimistic submit rollback. */
  setDraftForFile: (prKey: string, filename: string, draft: PrV2FileDraftState | null) => void;
  clearDraftForFile: (prKey: string, filename: string) => void;
  /** Removes drafts for files no longer present in the currently loaded PR payload. */
  pruneDraftsToExistingFiles: (prKey: string, filenames: string[]) => void;
  clearDraftsForPr: (prKey: string) => void;
};

/** Shared empty draft object returned when a file has no stored draft yet. */
const EMPTY_FILE_DRAFT_STATE: PrV2FileDraftState = {
  comment: '',
  line: '',
  side: null,
  startLine: '',
  startSide: null,
  selectedLines: null,
  selectionOnly: false,
};

/** Returns the stable per-PR namespace used by the draft store. */
export function getPrV2DraftStoreKey(owner: string, repo: string, number: string): string {
  return `${owner}/${repo}/${number}`;
}

/** Safely parse positive integer line values stored as string/number draft fields. */
export function asPositiveInteger(value: unknown): number | null {
  if (typeof value === 'number' && Number.isInteger(value) && value > 0) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10);
    if (Number.isInteger(parsed) && parsed > 0) {
      return parsed;
    }
  }

  return null;
}

/** Cheap structural comparison used to avoid replacing unchanged selection objects. */
function areSelectedLineRangesEqual(
  left: SelectedLineRange | null,
  right: SelectedLineRange | null,
): boolean {
  if (left === right) {
    return true;
  }

  if (!left || !right) {
    return false;
  }

  return (
    left.start === right.start &&
    left.end === right.end &&
    left.side === right.side &&
    left.endSide === right.endSide
  );
}

/** Equality guard so store writes preserve object identity when no data actually changed. */
function areDraftsEqual(left: PrV2FileDraftState, right: PrV2FileDraftState): boolean {
  return (
    left.comment === right.comment &&
    left.line === right.line &&
    left.side === right.side &&
    left.startLine === right.startLine &&
    left.startSide === right.startSide &&
    areSelectedLineRangesEqual(left.selectedLines, right.selectedLines) &&
    Boolean(left.selectionOnly) === Boolean(right.selectionOnly)
  );
}

function isDraftStateEmpty(draft: PrV2FileDraftState): boolean {
  return (
    draft.comment === '' &&
    draft.line === '' &&
    draft.side == null &&
    draft.startLine === '' &&
    draft.startSide == null &&
    draft.selectedLines == null &&
    !draft.selectionOnly
  );
}

function normalizeSelectedLineRange(range: SelectedLineRange): {
  line: string;
  side: PrV2GithubReviewSide;
  startLine: string;
  startSide: PrV2GithubReviewSide | null;
} {
  const isAscendingSelection = range.start <= range.end;
  const startLine = isAscendingSelection ? range.start : range.end;
  const endLine = isAscendingSelection ? range.end : range.start;
  const rawStartSide: PrV2GithubReviewSide = range.side === 'deletions' ? 'LEFT' : 'RIGHT';
  const rawEndSide: PrV2GithubReviewSide =
    (range.endSide ?? range.side ?? 'additions') === 'deletions' ? 'LEFT' : 'RIGHT';
  const startSide = isAscendingSelection ? rawStartSide : rawEndSide;
  const endSide = isAscendingSelection ? rawEndSide : rawStartSide;

  return {
    line: String(endLine),
    side: endSide,
    startLine: startLine !== endLine ? String(startLine) : '',
    startSide: startLine !== endLine ? startSide : null,
  };
}

/** Returns next state with a file draft inserted/replaced for a PR namespace. */
function withUpdatedDraftForFile(
  draftsByPr: PrV2DraftsByPr,
  prKey: string,
  filename: string,
  nextDraft: PrV2FileDraftState,
): PrV2DraftsByPr {
  const draftsByFile = draftsByPr[prKey] ?? null;
  const currentDraft = draftsByFile?.[filename];

  // Preserve object identity when nothing changed so subscribers avoid unnecessary re-renders.
  if (currentDraft && areDraftsEqual(currentDraft, nextDraft)) {
    return draftsByPr;
  }

  return {
    ...draftsByPr,
    [prKey]: {
      ...(draftsByFile ?? {}),
      [filename]: nextDraft,
    },
  };
}

/** Returns next state with one file draft removed, cleaning up empty PR namespaces. */
function withClearedDraftForFile(
  draftsByPr: PrV2DraftsByPr,
  prKey: string,
  filename: string,
): PrV2DraftsByPr {
  const draftsByFile = draftsByPr[prKey];
  if (!draftsByFile || !(filename in draftsByFile)) {
    return draftsByPr;
  }

  const nextDraftsByFile = { ...draftsByFile };
  delete nextDraftsByFile[filename];

  if (Object.keys(nextDraftsByFile).length === 0) {
    const nextDraftsByPr = { ...draftsByPr };
    delete nextDraftsByPr[prKey];
    return nextDraftsByPr;
  }

  return {
    ...draftsByPr,
    [prKey]: nextDraftsByFile,
  };
}

/** Reads stored draft for a file, falling back to the shared empty draft shape. */
function getDraftStateForFile(
  draftsByPr: PrV2DraftsByPr,
  prKey: string,
  filename: string,
): PrV2FileDraftState {
  return draftsByPr[prKey]?.[filename] ?? EMPTY_FILE_DRAFT_STATE;
}

export const usePrV2DraftStore = create<PrV2DraftStore>((set, get) => ({
  draftsByPr: {},
  getDraft: (prKey, filename) => get().draftsByPr[prKey]?.[filename],
  openFileDraft: (prKey, filename) => {
    set((state) => {
      const currentDraft = getDraftStateForFile(state.draftsByPr, prKey, filename);
      const nextDraft: PrV2FileDraftState = {
        comment: currentDraft.comment ?? '',
        line: '',
        side: null,
        startLine: '',
        startSide: null,
        selectedLines: null,
        selectionOnly: false,
      };

      const nextDraftsByPr = withUpdatedDraftForFile(state.draftsByPr, prKey, filename, nextDraft);
      if (nextDraftsByPr === state.draftsByPr) {
        return state;
      }

      return {
        draftsByPr: nextDraftsByPr,
      };
    });
  },
  toggleLineDraft: (prKey, filename, lineNumber, side) => {
    set((state) => {
      const currentDraft = getDraftStateForFile(state.draftsByPr, prKey, filename);
      const currentLine = asPositiveInteger(currentDraft.line);
      const isSameLineAndSide = currentLine === lineNumber && currentDraft.side === side;
      const isSelectionOnlyDraft = Boolean(currentDraft.selectionOnly);
      const shouldCloseDraft = isSameLineAndSide && !isSelectionOnlyDraft;
      const shouldPromoteSelection = isSameLineAndSide && isSelectionOnlyDraft;

      // Clicking the active inline anchor again behaves as "close composer for this file".
      const nextDraftsByPr = shouldCloseDraft
        ? withClearedDraftForFile(state.draftsByPr, prKey, filename)
        : withUpdatedDraftForFile(state.draftsByPr, prKey, filename, {
            ...currentDraft,
            comment: currentDraft.comment ?? '',
            line: shouldPromoteSelection ? currentDraft.line : String(lineNumber),
            side: shouldPromoteSelection ? currentDraft.side : side,
            startLine: shouldPromoteSelection ? currentDraft.startLine : '',
            startSide: shouldPromoteSelection ? currentDraft.startSide : null,
            selectedLines: shouldPromoteSelection ? currentDraft.selectedLines : null,
            selectionOnly: false,
          });

      if (nextDraftsByPr === state.draftsByPr) {
        return state;
      }

      return {
        draftsByPr: nextDraftsByPr,
      };
    });
  },
  setLineSelection: (prKey, filename, range) => {
    set((state) => {
      const currentDraft = getDraftStateForFile(state.draftsByPr, prKey, filename);

      if (!range) {
        if (currentDraft.selectedLines == null) {
          return state;
        }

        const nextDraft: PrV2FileDraftState = {
          ...currentDraft,
          ...(currentDraft.selectionOnly
            ? {
                line: '',
                side: null,
                startLine: '',
                startSide: null,
              }
            : {}),
          selectedLines: null,
          selectionOnly: false,
        };

        const nextDraftsByPr = isDraftStateEmpty(nextDraft)
          ? withClearedDraftForFile(state.draftsByPr, prKey, filename)
          : withUpdatedDraftForFile(state.draftsByPr, prKey, filename, nextDraft);
        if (nextDraftsByPr === state.draftsByPr) {
          return state;
        }

        return {
          draftsByPr: nextDraftsByPr,
        };
      }

      const nextDraft: PrV2FileDraftState = {
        ...currentDraft,
        comment: currentDraft.comment ?? '',
        ...normalizeSelectedLineRange(range),
        selectedLines: range,
        selectionOnly: true,
      };

      const nextDraftsByPr = withUpdatedDraftForFile(state.draftsByPr, prKey, filename, nextDraft);
      if (nextDraftsByPr === state.draftsByPr) {
        return state;
      }

      return {
        draftsByPr: nextDraftsByPr,
      };
    });
  },
  setDraftComment: (prKey, filename, value) => {
    set((state) => {
      const currentDraft = getDraftStateForFile(state.draftsByPr, prKey, filename);
      const nextDraft: PrV2FileDraftState = {
        ...currentDraft,
        comment: value,
      };

      const nextDraftsByPr = withUpdatedDraftForFile(state.draftsByPr, prKey, filename, nextDraft);
      if (nextDraftsByPr === state.draftsByPr) {
        return state;
      }

      return {
        draftsByPr: nextDraftsByPr,
      };
    });
  },
  setDraftForFile: (prKey, filename, draft) => {
    set((state) => {
      if (!draft) {
        const nextDraftsByPr = withClearedDraftForFile(state.draftsByPr, prKey, filename);
        if (nextDraftsByPr === state.draftsByPr) {
          return state;
        }

        return {
          draftsByPr: nextDraftsByPr,
        };
      }

      const nextDraftsByPr = withUpdatedDraftForFile(state.draftsByPr, prKey, filename, draft);
      if (nextDraftsByPr === state.draftsByPr) {
        return state;
      }

      return {
        draftsByPr: nextDraftsByPr,
      };
    });
  },
  clearDraftForFile: (prKey, filename) => {
    set((state) => {
      const nextDraftsByPr = withClearedDraftForFile(state.draftsByPr, prKey, filename);
      if (nextDraftsByPr === state.draftsByPr) {
        return state;
      }

      return {
        draftsByPr: nextDraftsByPr,
      };
    });
  },
  pruneDraftsToExistingFiles: (prKey, filenames) => {
    set((state) => {
      const draftsByFile = state.draftsByPr[prKey];
      if (!draftsByFile) {
        return state;
      }

      const existingFilenames = new Set(filenames);
      let hasChanges = false;
      const nextDraftsByFile: PrV2DraftsByFile = {};

      Object.entries(draftsByFile).forEach(([filename, draft]) => {
        // Drop drafts for files no longer present in the latest ordered-files payload.
        if (!existingFilenames.has(filename)) {
          hasChanges = true;
          return;
        }

        nextDraftsByFile[filename] = draft;
      });

      if (!hasChanges) {
        return state;
      }

      if (Object.keys(nextDraftsByFile).length === 0) {
        const nextDraftsByPr = { ...state.draftsByPr };
        delete nextDraftsByPr[prKey];
        return {
          draftsByPr: nextDraftsByPr,
        };
      }

      return {
        draftsByPr: {
          ...state.draftsByPr,
          [prKey]: nextDraftsByFile,
        },
      };
    });
  },
  clearDraftsForPr: (prKey) => {
    set((state) => {
      if (!(prKey in state.draftsByPr)) {
        return state;
      }

      const nextDraftsByPr = { ...state.draftsByPr };
      delete nextDraftsByPr[prKey];
      return {
        draftsByPr: nextDraftsByPr,
      };
    });
  },
}));
